import useSWR from 'swr'
import { AxiosResponse } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { APIDecoratorWithBaseURI } from '../../Service/api'

// Espelha o body de GET /bordero (OP-3437) do backend analytics.

export interface BorderoEventoRow {
  eventoId: number
  nome: string
  praca: string | null
  uf: string | null
  pagos: number
  cortesias: number
  valorFace: number
  ticketMedio: number
  conveniencia: number
  juros: number
  custoAdquirencia: number
  seguro: number
  taxaProcessamento: number
  totalCompras: number
}

export interface BorderoTotais {
  pagos: number
  cortesias: number
  valorFace: number
  ticketMedio: number
  conveniencia: number
  juros: number
  custoAdquirencia: number
  seguro: number
  taxaProcessamento: number
  totalCompras: number
  pedidosPagos: number
  pedidosComSeguro: number
  transacionado: number
  naoTransacionado: number
  transacionadoFace: number
  naoTransacionadoFace: number
  pedidosNaoTransacionados: number
}

export interface BorderoMetodoRow {
  categoria: string
  label: string
  total: number
  face?: number
  pedidos: number
  bilhetes: number
}

export interface BorderoParcela {
  parcelas: number
  pedidos: number
  total: number
  face?: number
}

export interface BorderoSemanaRow {
  semana: string
  inicio: string
  pedidos: number
  bilhetes: number
  face: number
  total: number
}

export interface BorderoDiaRow {
  dia: string // YYYY-MM-DD
  pedidos: number
  bilhetes: number
  face: number
  total: number
}

export interface BorderoComplementares {
  meiaEntrada: { qtd: number; valor: number }
  inteira: { qtd: number; valor: number }
  cancelamentos: { qtd: number; valor: number }
  compradoresUnicos: number
}

export interface BorderoProdutorRow {
  clienteId: number // 0 = linha sintética "Outros produtores"
  nome: string
  eventos: number
  pagos: number
  ticketMedio: number
  totalCompras: number
}

export interface BorderoMeta {
  eventosSolicitados: number[]
  canais: string[]
  periodo: { inicio: string | null; fim: string | null }
  somaComponentes: number
  totalCompras: number
  diferencaReconciliacao: number
  nota: string
  modo?: 'evento' | 'periodo'
  totalEventos?: number
  topN?: number
}

export interface BorderoBody {
  eventos: BorderoEventoRow[] // modo período: Top N + linha "Outros" (eventoId 0)
  produtores?: BorderoProdutorRow[] // modo período: Top N produtores + "Outros"
  totais: BorderoTotais
  metodos: BorderoMetodoRow[]
  parcelas: BorderoParcela[]
  curvaSemanal: BorderoSemanaRow[]
  curvaDiaria: BorderoDiaRow[]
  complementares: BorderoComplementares
  meta: BorderoMeta
}

/** Janela máxima do consolidado global síncrono; acima disso vai para o job assíncrono. */
export const BORDERO_MAX_DIAS_SYNC = 45
/** Teto do job assíncrono (12 meses). */
export const BORDERO_MAX_DIAS_ASYNC = 366

export interface BorderoJobState {
  status: 'running' | 'failed'
  progress?: { done: number; total: number } | null
  error?: string | null
}

interface UseBorderoResult {
  bordero?: BorderoBody
  borderoLoading: boolean
  borderoError: any
  /** Estado do job assíncrono (consolidado global > 45 dias); null fora dele ou quando pronto. */
  borderoJob: BorderoJobState | null
  /** Descarta um job com falha e dispara o processamento de novo. */
  borderoRetry: () => void
}

/** Dias entre duas datas ISO (YYYY-MM-DD); 0 sem janela completa. */
export const borderoDias = (start?: string, end?: string): number =>
  start && end ? (Date.parse(end) - Date.parse(start)) / 86_400_000 : 0

/**
 * Relatório de performance de vendas (Borderô) de um GRUPO de eventos (turnê).
 * Envia `eventIds=csv`. Desativa o fetch enquanto não houver evento selecionado.
 * Janela opcional [startDate, endDate]; por padrão o período completo.
 */
export interface BorderoPdv {
  id: number
  nome: string
}

export interface BorderoIngressoRow {
  ingressoId: number
  nome: string
  tipo: string
  pagos: number
  cortesias: number
  valorFace: number
}

export interface BorderoSetorRow {
  setorId: number
  setor: string
  pagos: number
  cortesias: number
  valorFace: number
  ingressos: BorderoIngressoRow[]
}

export interface UseBorderoOpts {
  eventIds: number[]
  clienteId?: number // "Todos os eventos" do produtor
  startDate?: string
  endDate?: string
  pdvIds?: number[]
  ignorePracaTestes?: boolean
}

const cleanIds = (arr?: number[]) =>
  (arr || []).map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0)

export const useBordero = (opts: UseBorderoOpts): UseBorderoResult => {
  const { clienteId, startDate, endDate } = opts
  const events = cleanIds(opts.eventIds)
  const pdvs = cleanIds(opts.pdvIds)

  // Seleção por evento, por produtor, ou — modo período — apenas por janela.
  const parts: string[] = []
  if (events.length) parts.push(`eventIds=${events.join(',')}`)
  else if (clienteId && clienteId > 0) parts.push(`clienteId=${clienteId}`)
  const hasPeriod = !!(startDate && endDate)
  if (hasPeriod) parts.push(`startDate=${startDate}`, `endDate=${endDate}`)
  if (pdvs.length) parts.push(`pdvIds=${pdvs.join(',')}`)
  if (opts.ignorePracaTestes === false) parts.push('ignorePracaTestes=0')

  // Habilita a consulta quando há evento, produtor ("todos") ou janela (período).
  const hasSelection = events.length > 0 || (!!clienteId && clienteId > 0) || hasPeriod

  // Consolidado global (sem evento e sem produtor) acima de 45 dias: o cálculo
  // não cabe nos 29s do API Gateway → /borderoJob (worker assíncrono) + polling.
  const isGlobal = events.length === 0 && !(clienteId && clienteId > 0) && hasPeriod
  const forceJob = isGlobal && borderoDias(startDate, endDate) > BORDERO_MAX_DIAS_SYNC

  // Fallback: seleções grandes (ex.: turnê com dezenas de eventos, sem data)
  // também estouram o síncrono — quando ele falha com 5xx/timeout, refazemos a
  // MESMA seleção via job assíncrono. Guarda a query exata para o fallback
  // morrer sozinho quando os filtros mudarem.
  const baseQuery = parts.join('&')
  const [jobFallbackFor, setJobFallbackFor] = useState<string | null>(null)
  const useJob = forceJob || jobFallbackFor === baseQuery

  const url = hasSelection
    ? `producer/analytics/${useJob ? 'borderoJob' : 'bordero'}?${baseQuery}`
    : null

  const { data, error, isLoading, mutate } = useSWR<AxiosResponse>(
    url,
    (u: string) => APIDecoratorWithBaseURI().get(u),
    {
      // Polling só enquanto o job processa; para em done/failed.
      refreshInterval: useJob
        ? (latest: any) => {
            const st = (latest as any)?.data?.body?.status ?? (latest as any)?.data?.data?.status
            return st === 'running' ? 4000 : 0
          }
        : 0,
    }
  )

  // Dispara o fallback quando o síncrono falha por 5xx/timeout (sem status =
  // rede/timeout). 4xx (permissão, parâmetros) não muda de caminho — o job
  // devolveria o mesmo erro.
  useEffect(() => {
    if (!error || useJob || !hasSelection) return
    const st = (error as any)?.response?.status
    if (!st || st >= 500) setJobFallbackFor(baseQuery)
  }, [error, useJob, hasSelection, baseQuery])

  const raw: any = useMemo(() => data?.data?.body ?? data?.data?.data ?? undefined, [data])

  const body: BorderoBody | undefined = useJob
    ? raw?.status === 'done'
      ? raw.result
      : undefined
    : raw

  const job: BorderoJobState | null =
    useJob && (raw?.status === 'running' || raw?.status === 'failed')
      ? { status: raw.status, progress: raw.progress ?? null, error: raw.error ?? null }
      : null

  // Retry: uma chamada única com retry=1 (descarta o job failed e redispara);
  // depois revalida a URL de polling normal.
  const borderoRetry = useCallback(() => {
    if (!url || !useJob) return
    APIDecoratorWithBaseURI()
      .get(`${url}&retry=1`)
      .finally(() => mutate())
  }, [url, useJob, mutate])

  const loading = isLoading || job?.status === 'running'

  return useMemo(
    () => ({
      bordero: body,
      borderoLoading: loading,
      borderoError: error,
      borderoJob: job,
      borderoRetry,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [body, loading, error, raw]
  )
}

/** Drill-down: vendas de um evento por setor e ingresso (lazy, só ao expandir). */
export const useBorderoSetores = (
  eventId: number | null,
  opts: { startDate?: string; endDate?: string; pdvIds?: number[]; ignorePracaTestes?: boolean }
): { setores: BorderoSetorRow[]; setoresLoading: boolean } => {
  const pdvs = cleanIds(opts.pdvIds)
  const dateQs =
    opts.startDate && opts.endDate ? `&startDate=${opts.startDate}&endDate=${opts.endDate}` : ''
  const pdvQs = pdvs.length ? `&pdvIds=${pdvs.join(',')}` : ''
  const testesQs = opts.ignorePracaTestes === false ? '&ignorePracaTestes=0' : ''
  const url =
    eventId && eventId > 0
      ? `producer/analytics/borderoSetores?eventId=${eventId}${dateQs}${pdvQs}${testesQs}`
      : null

  const { data, isLoading } = useSWR<AxiosResponse>(url, (u: string) =>
    APIDecoratorWithBaseURI().get(u)
  )
  const body: BorderoSetorRow[] = useMemo(() => data?.data?.body ?? data?.data?.data ?? [], [data])
  return useMemo(() => ({ setores: body, setoresLoading: isLoading }), [body, isLoading])
}

/** Lista de PDVs (com venda) dos eventos selecionados ou de todos do produtor. */
export const useBorderoPdvs = (
  eventIds: number[],
  clienteId?: number,
  ignorePracaTestes?: boolean
): { pdvs: BorderoPdv[]; pdvsLoading: boolean } => {
  const events = cleanIds(eventIds)
  const testesQs = ignorePracaTestes === false ? '&ignorePracaTestes=0' : ''
  const url = events.length
    ? `producer/analytics/borderoPdvs?eventIds=${events.join(',')}${testesQs}`
    : clienteId && clienteId > 0
    ? `producer/analytics/borderoPdvs?clienteId=${clienteId}${testesQs}`
    : null

  const { data, isLoading } = useSWR<AxiosResponse>(url, (u: string) =>
    APIDecoratorWithBaseURI().get(u)
  )

  const body: BorderoPdv[] = useMemo(
    () => data?.data?.body ?? data?.data?.data ?? [],
    [data]
  )

  return useMemo(() => ({ pdvs: body, pdvsLoading: isLoading }), [body, isLoading])
}
